import * as api from "@/api";
import styles from "@/components/about-popup.module.css";
import LoadingSkeletons from "@/components/loading-skeletons";
import PopupShell from "@/components/popup-shell";
import { globals } from "@/globals";
import { closePopup } from "@/utils";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import dayjs from "dayjs";
import { t } from "i18next";
import { JSX, UIEvent, useState } from "react";
import { Trans } from "react-i18next";

interface AboutPopupProps {
	closeAboutPopup?: () => void;
	isShown: boolean;
}

function AboutPopup({
	closeAboutPopup,
	isShown,
}: AboutPopupProps): JSX.Element {
	const { data: changelog } = useQuery({
		queryFn: api.getChangelog,
		queryKey: ["changelog"],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});

	const [isChangelogScrolled, setIsChangelogScrolled] =
		useState<boolean>(false);

	const lastUpdated =
		globals.LAST_UPDATED &&
		dayjs(globals.LAST_UPDATED).format("YYYY-MM-DD");

	if (!closeAboutPopup) {
		closeAboutPopup = (): void => {
			closePopup("about");
		};
	}

	const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
		const target = event.target as HTMLDivElement;
		setIsChangelogScrolled(target.scrollTop > 0);
	};

	const socialMediaList = [
		{
			icon: "\ue883",
			title: t("weibo"),
			url: "https://weibo.com/limestart",
		},
		{
			icon: "\ue611",
			title: t("bilibili"),
			url: "https://space.bilibili.com/14193369",
		},
		{
			icon: "\ue70e",
			title: t("rednote"),
			url: "https://www.xiaohongshu.com/user/profile/5ca8c8950000000016027810",
		},
		{
			icon: "\uec26",
			title: t("douyin"),
			url: "https://v.douyin.com/iUMppPKL/",
		},
	];

	const socialMediaItems = socialMediaList.map((item) => (
		<a
			className={clsx(styles["social-media-link"], styles["gray-link"])}
			href={item.url}
			key={item.title}
			rel="noreferrer"
			target="_blank"
			title={item.title}
		>
			<i>{item.icon}</i>
		</a>
	));

	return (
		<PopupShell
			isHeadingHidden={true}
			isPureWhite={true}
			isShown={isShown}
			onClose={closeAboutPopup}
			title={t("about")}
		>
			<div
				className={clsx(
					styles["logo"],
					isChangelogScrolled && styles["scrolled"],
				)}
			></div>
			<h1
				className={clsx(
					styles["name"],
					isChangelogScrolled && styles["scrolled"],
				)}
			>
				{t("limeStartPage")}
			</h1>
			<div
				className={clsx(
					styles["version"],
					isChangelogScrolled && styles["scrolled"],
				)}
			>
				{globals.VERSION}
			</div>
			<div
				className={clsx(
					styles["presented-by"],
					isChangelogScrolled && styles["scrolled"],
				)}
			>
				<Trans
					components={[
						<a
							className={clsx(
								"link",
								styles["fixed-theme-color"],
							)}
							href="https://maorx.cn/"
							key="Ruoxin Mao"
							target="_blank"
							rel="noreferrer"
						>
							Ruoxin Mao
						</a>,
						<a
							className={clsx(
								"link",
								styles["fixed-theme-color"],
							)}
							href="https://www.yangshangzhen.com/"
							key="Shangzhen Yang"
							target="_blank"
							rel="noreferrer"
						>
							Shangzhen Yang
						</a>,
					]}
					i18nKey="presentedBy"
				/>
			</div>
			<div
				className={clsx(
					styles["copyright"],
					isChangelogScrolled && styles["scrolled"],
				)}
			>
				&copy; {new Date().getFullYear()}{" "}
				<a
					className={styles["gray-link"]}
					href="https://www.retiehe.com/"
					rel="noreferrer"
					target="_blank"
				>
					{t("retinboxSoftware")}
				</a>
				<span className={styles["divider"]}></span>
				<a
					className={styles["gray-link"]}
					href="https://docs.retiehe.com/tos.html"
					rel="noreferrer"
					target="_blank"
				>
					{t("termsOfService")}
				</a>
				<span className={styles["divider"]}></span>
				<a
					className={styles["gray-link"]}
					href="https://docs.retiehe.com/privacy.html"
					rel="noreferrer"
					target="_blank"
				>
					{t("privacyPolicy")}
				</a>
			</div>
			<div
				className={clsx(
					styles["social-media-links"],
					isChangelogScrolled && styles["scrolled"],
				)}
			>
				{socialMediaItems}
			</div>
			<div
				className={clsx(
					styles["changelog"],
					isChangelogScrolled && styles["scrolled"],
				)}
				onScroll={handleScroll}
			>
				{changelog && (
					<>
						<p>
							{t("limeStartPageHasBeenLaunchedForDays", {
								days: globals.limeAge,
							})}
							<br />
							{t("lastUpdated")}
							{lastUpdated}
						</p>
						<div
							dangerouslySetInnerHTML={{
								__html: changelog,
							}}
						></div>
					</>
				)}
				{!changelog && (
					<LoadingSkeletons
						widths={[
							"120px",
							"120px",
							"0",
							"60px",
							"50%",
							undefined,
							undefined,
							"0",
							"60px",
							undefined,
							undefined,
						]}
					/>
				)}
			</div>
		</PopupShell>
	);
}

export default AboutPopup;
